import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { useJobTracker } from '@nucleus-commerce/admin-kit/jobs';
import type { OrganizationExportJob } from '@nucleus-commerce/api-client/platform';

import { organizationsApi } from '../api/organizations-api';

import { organizationKeys } from './organization-keys';

const isTerminal = (job: OrganizationExportJob) =>
  job.status === 'completed' || job.status === 'failed';

/**
 * Starts a full export and follows it with the bounded job poll until the signed link is ready.
 * The backend keeps no list of past exports, so the job lives as long as this screen.
 */
export function useOrganizationExport(organizationId: string) {
  const [started, setStarted] = useState<{ id: string; at: number } | null>(null);
  const start = useMutation({
    mutationFn: () => organizationsApi.startExport(organizationId),
    meta: { successMessage: 'organizations:toasts.exportStarted' },
    onSuccess: (job) => setStarted({ id: job.id, at: Date.now() }),
  });
  const tracker = useJobTracker<OrganizationExportJob>({
    jobId: started?.id,
    startedAt: started?.at,
    fetchJob: (jobId) => organizationsApi.getExport(organizationId, jobId),
    isTerminal,
    queryKey: organizationKeys.exportJobs(organizationId),
    // A zip of every store's orders can take a few minutes for a large tenant.
    policy: { timeoutMs: 5 * 60_000 },
  });
  return { start, tracker, job: tracker.job ?? start.data };
}
